// V1.40.5 · Configuración → Contabilidad → Cuentas Principales y Tipos
// Panel de gestión del plan contable global + entradas locales.
// Reglas:
//   • Cuentas semilla (is_seed=true / section_id="__global__") son de
//     sólo-lectura. Se muestran con badge "Global" y sin botón X.
//   • Cuentas locales (creadas por el usuario) pueden desactivarse con
//     PIN maestro (soft delete via DELETE /accounting/accounts/:id).
//   • Añadir una cuenta principal significa crear al menos un tipo bajo
//     esa cuenta (POST /accounting/accounts con main + kind).
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Loader2, Lock, ShieldCheck, RotateCcw, EyeOff } from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";

export default function AccountingAccountsSettings() {
  const pin = usePinSecurity();
  const [tree, setTree] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newMainOpen, setNewMainOpen] = useState(false);
  const [newMain, setNewMain] = useState("");
  const [newMainFirstKind, setNewMainFirstKind] = useState("");
  const [addKindFor, setAddKindFor] = useState("");   // main donde se añade
  const [newKind, setNewKind] = useState("");
  const [busy, setBusy] = useState(false);
  // V1.79 · El borrado es lógico: hay que poder VER y REACTIVAR lo eliminado.
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/accounts");
      setTree(data?.tree || {});
      setTotal(data?.total || 0);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error cargando cuentas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createEntry = async (main, kind) => {
    setBusy(true);
    try {
      const { data } = await api.post("/accounting/accounts", { main, kind, active: true });
      toast.success(data?.reactivated
        ? `Reactivado: ${main} → ${kind} (ya existía desactivado)`
        : `Añadido: ${main} → ${kind}`);
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err?.message || "Error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitNewMain = async () => {
    const m = newMain.trim();
    const k = newMainFirstKind.trim();
    if (!m || !k) {
      toast.error("Introduce cuenta principal y al menos un tipo");
      return;
    }
    const ok = await createEntry(m, k);
    if (ok) {
      setNewMain("");
      setNewMainFirstKind("");
      setNewMainOpen(false);
    }
  };

  const submitNewKind = async (main) => {
    const k = newKind.trim();
    if (!k) {
      toast.error("Introduce el nombre del tipo");
      return;
    }
    const ok = await createEntry(main, k);
    if (ok) {
      setNewKind("");
      setAddKindFor("");
    }
  };

  const removeEntry = async (entry) => {
    if (entry.is_seed || entry.section_id === "__global__") {
      toast.info("Las cuentas globales del plan contable no se pueden eliminar");
      return;
    }
    try {
      await pin.require("accounting.account.delete");
      if (!window.confirm(`¿Desactivar "${entry.main} → ${entry.kind}"?`)) return;
      await api.delete(`/accounting/accounts/${entry.id}`);
      toast.success("Cuenta desactivada");
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  const reactivateEntry = async (entry) => {
    setBusy(true);
    try {
      await api.post(`/accounting/accounts/${entry.id}/reactivate`);
      toast.success(`Reactivado: ${entry.main} → ${entry.kind}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo reactivar");
    } finally {
      setBusy(false);
    }
  };

  const inactiveCount = Object.values(tree).flat().filter((e) => e.active === false).length;

  const sortedMains = Object.keys(tree).sort((a, b) => a.localeCompare(b, "es"));

  return (
    <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5 space-y-4" data-testid="settings-accounting-accounts">
      <header className="flex items-center gap-2">
        <div className="rounded-lg p-1.5 bg-emerald-100 text-emerald-700"><Wallet className="h-4 w-4" /></div>
        <div>
          <h2 className="text-sm font-bold text-neutral-800">Cuentas Principales y Tipos de Gasto</h2>
          <p className="text-[11px] text-neutral-500">
            Plan contable global (compartido entre secciones) + entradas locales.
            Las cuentas <span className="text-emerald-700 font-semibold">Globales</span> no se pueden eliminar.
            Las eliminadas se desactivan, no se borran: puedes volver a verlas y reactivarlas.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setShowInactive((v) => !v)}
                  className={"inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors " +
                    (showInactive ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                                  : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50")}
                  data-testid="accounting-toggle-inactive">
            <EyeOff className="h-3 w-3" />
            {showInactive ? "Ocultar eliminadas" : `Ver eliminadas${inactiveCount ? ` (${inactiveCount})` : ""}`}
          </button>
          <span className="text-[11px] text-neutral-500">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `${total} entradas`}
          </span>
        </div>
      </header>

      {loading ? (
        <div className="py-8 text-center text-sm text-neutral-500 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : (
        <div className="space-y-3">
          {sortedMains.map((main) => {
            const all = tree[main] || [];
            const entries = showInactive ? all : all.filter((e) => e.active !== false);
            if (!entries.length) return null;
            const hasSeed = all.some((e) => e.is_seed || e.section_id === "__global__");
            return (
              <div key={main} className="rounded-xl border border-neutral-200 bg-neutral-50/50">
                <div className="px-4 py-2.5 border-b border-neutral-200 flex items-center gap-2">
                  <div className="font-bold text-sm text-neutral-800">{main}</div>
                  {hasSeed && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                      <ShieldCheck className="h-3 w-3" /> Global
                    </span>
                  )}
                  <div className="ml-auto text-[11px] text-neutral-400">
                    {entries.length} tipo{entries.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="p-3 flex flex-wrap gap-1.5">
                  {entries.map((e) => {
                    const protected_ = e.is_seed || e.section_id === "__global__";
                    return (
                      <div key={e.id}
                           className={
                             "inline-flex items-center gap-1 rounded-full text-[11px] px-2.5 py-1 border " +
                             (protected_
                               ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                               : (e.active ? "bg-white border-neutral-300 text-neutral-700"
                                           : "bg-neutral-100 border-neutral-200 text-neutral-400 line-through"))
                           }
                           data-testid={`accounting-kind-${e.id}`}>
                        <span className="font-medium">{e.kind}</span>
                        {protected_ ? (
                          <Lock className="h-3 w-3 opacity-60" />
                        ) : e.active === false ? (
                          <button type="button" onClick={() => reactivateEntry(e)} disabled={busy}
                                  className="ml-0.5 rounded-full hover:bg-emerald-100 hover:text-emerald-700 p-0.5"
                                  title="Reactivar esta cuenta"
                                  data-testid={`accounting-kind-reactivate-${e.id}`}>
                            <RotateCcw className="h-3 w-3" />
                          </button>
                        ) : (
                          <button type="button" onClick={() => removeEntry(e)}
                                  className="ml-0.5 rounded-full hover:bg-red-100 hover:text-red-600 p-0.5"
                                  title="Desactivar"
                                  data-testid={`accounting-kind-delete-${e.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {addKindFor === main ? (
                    <div className="inline-flex items-center gap-1 rounded-full bg-white border border-orange-300 pl-2 pr-1 py-0.5"
                         data-testid={`accounting-kind-add-form-${main}`}>
                      <input
                        autoFocus
                        value={newKind}
                        onChange={(e) => setNewKind(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") submitNewKind(main);
                          if (e.key === "Escape") { setAddKindFor(""); setNewKind(""); }
                        }}
                        placeholder="Nombre del tipo"
                        className="text-[11px] outline-none w-32"
                        data-testid={`accounting-kind-add-input-${main}`}
                      />
                      <button type="button" onClick={() => submitNewKind(main)} disabled={busy}
                              className="rounded-full bg-orange-500 hover:bg-orange-600 text-white p-1"
                              data-testid={`accounting-kind-add-submit-${main}`}>
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => { setAddKindFor(main); setNewKind(""); }}
                            className="inline-flex items-center gap-1 rounded-full text-[11px] px-2.5 py-1 border border-dashed border-neutral-300 text-neutral-500 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50"
                            data-testid={`accounting-kind-add-btn-${main}`}>
                      <Plus className="h-3 w-3" /> Añadir tipo
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Bloque añadir cuenta principal */}
          {newMainOpen ? (
            <div className="rounded-xl border-2 border-dashed border-orange-300 bg-orange-50/40 p-4 space-y-2"
                 data-testid="accounting-newmain-form">
              <div className="text-xs font-bold text-neutral-800">Nueva cuenta principal</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  value={newMain}
                  onChange={(e) => setNewMain(e.target.value)}
                  placeholder="Cuenta principal (ej. Franquicias)"
                  className="text-xs rounded border border-neutral-300 px-2 py-1.5 bg-white"
                  data-testid="accounting-newmain-name"
                />
                <input
                  value={newMainFirstKind}
                  onChange={(e) => setNewMainFirstKind(e.target.value)}
                  placeholder="Primer tipo (ej. Comisiones)"
                  className="text-xs rounded border border-neutral-300 px-2 py-1.5 bg-white"
                  data-testid="accounting-newmain-firstkind"
                />
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={submitNewMain} disabled={busy}
                        className="btn-primary !h-8 !px-3 !text-xs"
                        data-testid="accounting-newmain-submit">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  Crear
                </button>
                <button type="button" onClick={() => { setNewMainOpen(false); setNewMain(""); setNewMainFirstKind(""); }}
                        className="btn-secondary !h-8 !px-3 !text-xs"
                        data-testid="accounting-newmain-cancel">
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setNewMainOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-neutral-300 hover:border-orange-400 hover:bg-orange-50/40 py-3 text-xs font-semibold text-neutral-500 hover:text-orange-600"
                    data-testid="accounting-newmain-open">
              <Plus className="h-4 w-4" /> Añadir cuenta principal
            </button>
          )}
        </div>
      )}
    </section>
  );
}
